/** Depth (number of parent hops) from `rootKey` to each body key in the subtree. */
function buildDepthMapFromRoot(bodies, rootKey) {
  const depth = new Map();
  if (!bodies || !rootKey || !bodies[rootKey]) return depth;
  const queue = [[String(rootKey), 0]];
  const seen = new Set();
  while (queue.length > 0) {
    const [key, d] = queue.shift();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    depth.set(key, d);
    const body = bodies[key];
    if (!body || !Array.isArray(body.children)) continue;
    for (const ch of body.children) {
      const childKey = typeof ch === "string" ? ch : ch?.uuid || ch;
      if (!childKey || seen.has(childKey)) continue;
      queue.push([String(childKey), d + 1]);
    }
  }
  return depth;
}

const ATTACHMENT_SITE_TOKEN_RE = /(^|_)attachment_site($|[_\d].*)/;

export function isAttachmentSiteName(siteName) {
  return ATTACHMENT_SITE_TOKEN_RE.test(String(siteName || ""));
}

/**
 * Prefer the shallowest `attachment_site` on a given world root (matches attach-marker logic).
 *
 * @param {Record<string, any>} bodies
 * @param {Array<{ siteUuid: string; bodyUuid: string; worldBodyUuid: string; siteName?: string }>} attachmentSites
 * @param {string} worldRootKey — bodies / worldBodies map key for the object root
 * @param {string} [siteName]
 * @returns {string | null} siteUuid
 */
export function pickShallowAttachmentSiteUuidForWorldRoot(
  bodies,
  attachmentSites,
  worldRootKey,
  siteName = "attachment_site"
) {
  if (!bodies || !worldRootKey || !Array.isArray(attachmentSites) || attachmentSites.length === 0) {
    return null;
  }
  const root = String(worldRootKey);
  const candidates = attachmentSites.filter(
    (s) =>
      s &&
      String(s.worldBodyUuid) === root &&
      (siteName == null || !s.siteName || s.siteName === siteName) &&
      s.bodyUuid &&
      s.siteUuid
  );
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0].siteUuid;

  const depthMap = buildDepthMapFromRoot(bodies, root);
  let best = candidates[0];
  let bestD = depthMap.has(String(best.bodyUuid)) ? depthMap.get(String(best.bodyUuid)) : Infinity;
  for (let i = 1; i < candidates.length; i += 1) {
    const c = candidates[i];
    const d = depthMap.has(String(c.bodyUuid)) ? depthMap.get(String(c.bodyUuid)) : Infinity;
    if (d < bestD) {
      best = c;
      bestD = d;
    } else if (d === bestD && String(c.siteUuid) < String(best.siteUuid)) {
      best = c;
    }
  }
  return best.siteUuid;
}

/**
 * Library drag: attachment_site markers should favor the robot flange (or
 * shallowest mount) on each world-body tree, not deeper sites on an already
 * mounted gripper that shares the same articulated root.
 *
 * @param {Record<string, any>} bodies
 * @param {Array<{ siteUuid: string; bodyUuid: string; worldBodyUuid: string }>} attachmentSites
 */
export function getShallowAttachmentSitesForLibraryTargets(bodies, attachmentSites) {
  if (!bodies || !Array.isArray(attachmentSites) || attachmentSites.length === 0) {
    return [];
  }

  const byRoot = new Map();
  for (const site of attachmentSites) {
    if (!site?.worldBodyUuid || !site?.bodyUuid) continue;
    const root = String(site.worldBodyUuid);
    if (!byRoot.has(root)) byRoot.set(root, []);
    byRoot.get(root).push(site);
  }

  const out = [];
  for (const [, sites] of byRoot) {
    if (sites.length === 0) continue;
    const rootKey = String(sites[0].worldBodyUuid);
    const depthMap = buildDepthMapFromRoot(bodies, rootKey);
    let minDepth = Infinity;
    const depths = sites.map((s) => {
      const d = depthMap.has(String(s.bodyUuid)) ? depthMap.get(String(s.bodyUuid)) : Infinity;
      return d;
    });
    for (const d of depths) {
      if (Number.isFinite(d) && d < minDepth) minDepth = d;
    }
    if (!Number.isFinite(minDepth)) continue;
    for (let i = 0; i < sites.length; i += 1) {
      if (depths[i] === minDepth) out.push(sites[i]);
    }
  }
  return out;
}

/**
 * Show attachment-site drop hints for any catalog attachment (`category: "attachment"`).
 */
export function isLibraryGripperDrag(dragData) {
  if (!dragData || dragData.itemType !== "libraryObject" || !dragData.object) return false;
  return dragData.object.category === "attachment";
}
